/**
 * Variacao contra o periodo anterior: glifo + texto, nunca so cor.
 *
 * Decisoes nao obvias:
 *  1. O triangulo e decorativo; o texto diz "alta de 12,0%" para o leitor de
 *     tela e para quem nao distingue verde de vermelho.
 *  2. Taxa (bps) nao mostra variacao relativa -- "+15% na taxa" e ambiguo.
 *     Mostra "de 1,9% para 3,1%".
 *  3. `compacto` e a forma de celula de tabela e cartao: glifo + relativo, com
 *     a frase completa no `title`.
 *  4. Dinheiro (`centavos`) mostra a diferenca em reais. Tratar centavos como
 *     contagem escrevia "+306.750" onde a diferenca era R$ 3.067,50.
 */

import { formatBRLFromCents, formatCount, formatPercentFromBps } from '../../../lib/format';
import { variar } from '../desempenho';

const TITULO = 'Comparado com o período anterior de mesmo tamanho';

export function Variacao({
  atual,
  anterior,
  tipo = 'contagem',
  compacto = false,
  className = '',
}: {
  readonly atual: number;
  /** `null` = sem periodo anterior (janela "desde o início"). */
  readonly anterior: number | null;
  readonly tipo?: 'contagem' | 'bps' | 'centavos';
  readonly compacto?: boolean;
  readonly className?: string;
}): JSX.Element {
  const v = variar(atual, anterior);
  const base = `inline-flex flex-wrap items-center gap-1 text-xs tabular-nums ${className}`;

  if (v.direcao === 'sem_base') {
    return (
      <span className={`${base} text-ink-subtle`} title={TITULO}>
        {compacto ? '—' : 'sem período anterior'}
        {compacto ? <span className="sr-only">sem período anterior</span> : null}
      </span>
    );
  }
  if (v.direcao === 'igual') {
    return (
      <span className={`${base} text-ink-subtle`} title={TITULO}>
        <span aria-hidden="true">=</span> {compacto ? 'igual' : 'igual ao período anterior'}
      </span>
    );
  }

  const alta = v.direcao === 'alta';
  const cor = alta ? 'text-status-good' : 'text-status-critical';
  const glifo = alta ? '▲' : '▼';
  const palavra = alta ? 'alta' : 'queda';

  if (tipo === 'bps') {
    const texto = `de ${formatPercentFromBps(anterior ?? 0)} para ${formatPercentFromBps(atual)}`;
    return (
      <span className={`${base} ${cor}`} title={`${TITULO}: ${texto}`}>
        <span aria-hidden="true">{glifo}</span>
        <span className="sr-only">{palavra}:</span>
        <span>{compacto ? formatPercentFromBps(Math.abs(atual - (anterior ?? 0))).replace('%', ' p.p.') : texto}</span>
      </span>
    );
  }

  const absoluto = `${v.absoluta > 0 ? '+' : '−'}${tipo === 'centavos' ? formatBRLFromCents(Math.abs(v.absoluta)) : formatCount(Math.abs(v.absoluta))}`;
  const relativo = v.relativaBps === null ? null : formatPercentFromBps(Math.abs(v.relativaBps));
  const completo = relativo === null ? `${absoluto} (antes: 0)` : `${palavra} de ${relativo} (${absoluto})`;
  return (
    <span className={`${base} ${cor}`} title={`${TITULO}: ${completo}`}>
      <span aria-hidden="true">{glifo}</span>
      <span className="sr-only">{palavra} de</span>
      {compacto ? (
        <span>{relativo ?? absoluto}</span>
      ) : (
        <span>
          {relativo ?? absoluto} <span className="text-ink-subtle">{relativo === null ? '(antes: 0)' : `(${absoluto}) vs período anterior`}</span>
        </span>
      )}
    </span>
  );
}
